import { ProjectFormData, OTHER_OPTION_ID } from '../schemas/projectFormSchema';
import { ProjectDtoOut, ProjectSupplierDtoOut } from '@/models/out/ProjectDtoOut';
import { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import { ProjectExpenseFrequency } from '@/models/common/ProjectStatus';

/**
 * Turns the wizard's form state into the API payload, base64-encoding the attached files
 * (documents ride inside the JSON body, same as support requests — never multipart).
 *
 * The "אחר" sentinel is translated here rather than in the form: the API takes either an id
 * from the closed list or the free text, never both.
 */
export const mapFormDataToDto = async (data: ProjectFormData): Promise<ProjectDtoOut> => {
  const isOtherType = data.projectTypeId === OTHER_OPTION_ID;

  const documents: DocumentDtoOut[] = [];
  for (const doc of data.documents ?? []) {
    if (!doc.file) continue;
    const encoded = await FileReaderHelper.fileToDocumentDtoOut(doc.file, doc.description || doc.file.name);
    documents.push(...encoded);
  }

  const suppliers: ProjectSupplierDtoOut[] = (data.suppliers ?? []).map((supplier) => {
    const isOtherDomain = supplier.activityDomainId === OTHER_OPTION_ID;
    return {
      supplierId: supplier.supplierId ?? null,
      name: supplier.name?.trim() || null,
      taxId: supplier.taxId?.trim() || null,
      activityDomainId: isOtherDomain ? null : supplier.activityDomainId ?? null,
      activityDomainOther: isOtherDomain ? supplier.activityDomainOther?.trim() || null : null,
    };
  });

  return {
    // Zod defaults every field, but the form's input type keeps them optional, so the
    // fallbacks here are for the type system rather than for real missing values.
    name: data.name?.trim() ?? '',
    projectTypeId: isOtherType ? null : data.projectTypeId ?? null,
    projectTypeOther: isOtherType ? data.projectTypeOther?.trim() || null : null,
    description: data.description?.trim() || null,
    userComments: data.userComments?.trim() || null,
    legalTermsApproved: data.legalTermsApproved ?? false,
    // Unfilled expense rows are dropped: the wizard opens step 2 with one blank row, and a row
    // whose amount was never typed (undefined) or is not positive is not an expense. Expenses are
    // optional, so an untouched step 2 legitimately sends an empty list.
    expenses: (data.expenses ?? [])
      .filter((expense): expense is typeof expense & { amount: number } => (expense.amount ?? 0) > 0)
      .map((expense) => ({
        amount: expense.amount,
        // The form no longer asks for a reason (HAT-1105, 22/10/2026); the API field stays for
        // the rows written before, and is simply not filled in any more.
        reason: null,
        frequency: expense.frequency ?? ProjectExpenseFrequency.Monthly,
        validUntil: expense.validUntil || null,
      })),
    suppliers,
    documents,
  };
};
